import { teammateMessageActionCardKind } from '@littlebird/action-cards';
import { useStores } from '@littlebird/sdk';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { FEATURES, useFeature } from '@/features/feature-flags';
import { NEW_CHAT_ID } from '@/utils/constants';

import {
  hivemindChatQueryKey,
  hivemindChatQueryOptions,
} from '../hivemind.query';

import { latestHivemindIncomingMessageId } from './hivemind-conversations-messages';

import type { HivemindChat } from '../hivemind.types';

const NO_CHAT: HivemindChat = { threads: [] };

export function useHivemindConversations(chatId: string): HivemindChat {
  'use no memo';
  const hasFeature = useFeature();
  const { messages: messagesStore, actionCards } = useStores();
  const queryClient = useQueryClient();
  const enabled = hasFeature(FEATURES.HIVE) && chatId !== NEW_CHAT_ID;
  // A send or an answer changes who has answered, which only the server knows. A card's status and sync id change
  // when it is sent, an incoming message when an answer arrives.
  const activityKey = enabled
    ? [
        latestHivemindIncomingMessageId(
          messagesStore.getMessagesByThreadId(chatId)
        ),
        ...actionCards
          .getByChatId(chatId)
          .filter(
            (card) =>
              card.actionType === teammateMessageActionCardKind.actionType
          )
          .map((card) => `${card.id}:${card.status}:${card.syncId}`),
      ].join('|')
    : null;
  const { data } = useQuery({ ...hivemindChatQueryOptions(chatId), enabled });

  // A reply that arrives while an earlier refresh is still out cancels it, so the older answer never overwrites the count
  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: hivemindChatQueryKey(chatId),
    });
  }, [activityKey, chatId, queryClient]);

  return enabled ? (data ?? NO_CHAT) : NO_CHAT;
}
